import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionHeading from './SectionHeading';
import { getLocaleList } from '../utils/localeList';
import useReveal from '../hooks/useReveal';
import { CALENDAR_URL } from '../constants/contact';

/**
 * Section 05 — one client at a time: the question they arrive with, the
 * concrete cases behind it, and the packages that fit.
 *
 * This absorbed the old standalone "Use cases" section. Split across two
 * sections, a visitor had to work out for themselves which case belonged to
 * which client; the cases now sit inside the client they describe, which is the
 * only reason they're persuasive.
 *
 * Every panel stays in the DOM and inactive ones are closed with the `hidden`
 * attribute rather than unmounted. Conditional rendering would drop three of
 * the four clients out of the HTML entirely — the cases are the section's
 * densest indexable copy, and hiding them from crawlers to save a few nodes is
 * a bad trade. `hidden` also keeps them out of the accessibility tree, which
 * unmounting and `aria-hidden` would each only half do.
 *
 * A case carries an optional `client`: the ones we can name (Beans, Meru,
 * Seevcash) show it as a chip, because a named client is worth more than any
 * amount of copy. The key is simply absent on the cases with no client to name
 * rather than set to an empty string — locales.test.js requires the three
 * locales to agree on shape, not that every case has every key, and a blank
 * string would fail its no-empty-leaves assertion.
 *
 * Packages are referenced by name and resolved against `engagement.packages`,
 * so the deliverable text has exactly one home. A name that stops resolving is
 * caught by locales.test.js rather than silently rendering a gap.
 */
/**
 * El panel abierto al llegar: "Wallets, neobanks & crypto fintech".
 *
 * Es el segmento con más que mostrar —tres paquetes y los casos con cliente
 * nombrado— así que es el que mejor responde la pregunta que trae el visitante.
 * Startups, que era el primero por orden de lista, es justamente el único
 * segmento sin casos: abrir en él dejaba la sección estrenándose con su panel
 * más flaco.
 *
 * Es un índice y no un identificador porque los segmentos vienen de los
 * locales y no tienen ninguna clave estable: sus nombres están traducidos. El
 * orden sí es estable —`locales.test.js` exige que las tres listas tengan el
 * mismo largo y la misma forma—, así que el índice apunta al mismo segmento en
 * los tres idiomas. Si se reordena `whoWeHelp.segments`, hay que mover esto.
 */
const DEFAULT_SEGMENT_INDEX = 1;

const WhoWeHelp = () => {
  const { t } = useTranslation();
  const reveal = useReveal();
  const segments = getLocaleList(t, 'whoWeHelp.segments');
  const packages = getLocaleList(t, 'engagement.packages');
  const [activeIndex, setActiveIndex] = useState(DEFAULT_SEGMENT_INDEX);
  const tabRefs = useRef([]);

  const selectTab = (index) => {
    setActiveIndex(index);
    tabRefs.current[index]?.focus();
  };

  // Automatic activation (WAI-ARIA tabs pattern): arrows move selection and
  // focus together, so a keyboard user reads panels the same way a mouse user
  // clicks through them.
  const handleKeyDown = (event) => {
    const lastIndex = segments.length - 1;
    const nextIndex = {
      ArrowRight: activeIndex === lastIndex ? 0 : activeIndex + 1,
      ArrowLeft: activeIndex === 0 ? lastIndex : activeIndex - 1,
      Home: 0,
      End: lastIndex,
    }[event.key];

    if (nextIndex === undefined) return;
    event.preventDefault();
    selectTab(nextIndex);
  };

  const findPackage = (name) => packages.find((pkg) => pkg.name === name);

  return (
    <div id="who-we-help" className="mainCont relative z-30 pt-5 mt-[100px] lg4:mt-[150px]" ref={reveal} data-reveal>
      <SectionHeading
        eyebrow={t('whoWeHelp.eyebrow')}
        title={t('whoWeHelp.title')}
        intro={t('whoWeHelp.intro')}
      />

      <div
        role="tablist"
        aria-label={t('whoWeHelp.eyebrow')}
        onKeyDown={handleKeyDown}
        className="flex flex-wrap gap-3 mb-8"
      >
        {segments.map((segment, index) => (
          <button
            key={segment.name}
            ref={(node) => { tabRefs.current[index] = node; }}
            type="button"
            role="tab"
            id={`who-we-help-tab-${index}`}
            aria-controls={`who-we-help-panel-${index}`}
            aria-selected={index === activeIndex}
            tabIndex={index === activeIndex ? 0 : -1}
            onClick={() => selectTab(index)}
            className={`rounded-full border px-5 py-2.5 text-sm font-[500] transition ${
              index === activeIndex
                ? 'bg-main border-main text-black font-extrabold'
                : 'bg-white/5 border-white/20 text-white hover:border-main hover:text-main'
            }`}
          >
            {segment.name}
          </button>
        ))}
      </div>

      {segments.map((segment, index) => (
        <div
          key={segment.name}
          role="tabpanel"
          id={`who-we-help-panel-${index}`}
          aria-labelledby={`who-we-help-tab-${index}`}
          hidden={index !== activeIndex}
        >
          <p className="font-grotesk text-[24px] sm3:text-[30px] font-bold text-white leading-[125%]">
            “{segment.question}”
          </p>
          <p className="text-main-gray mt-3 max-w-[720px]">{segment.context}</p>

          {segment.cases.length > 0 ? (
            <div className="grid grid-cols-1 md2:grid-cols-2 gap-6 mt-8">
              {segment.cases.map((item) => (
                <div
                  key={item.component + item.problem}
                  className="landingCard p-6 flex flex-col"
                >
                  {item.client ? (
                    <div className="w-fit rounded-full bg-main/15 border border-main/40 px-3 py-1 mb-4">
                      <span className="font-grotesk font-bold text-main text-sm">{item.client}</span>
                    </div>
                  ) : null}

                  <div>
                    <div className="text-main-gray text-xs uppercase tracking-[1.5px]">
                      {t('whoWeHelp.labels.problem')}
                    </div>
                    <p className="text-white mt-1">{item.problem}</p>
                  </div>

                  <div className="mt-5">
                    <div className="text-main-gray text-xs uppercase tracking-[1.5px]">
                      {t('whoWeHelp.labels.solution')}
                    </div>
                    <p className="text-main-gray text-sm mt-1">{item.solution}</p>
                  </div>

                  <div className="mt-5 flex-grow">
                    <div className="text-main-gray text-xs uppercase tracking-[1.5px]">
                      {t('whoWeHelp.labels.component')}
                    </div>
                    <p className="font-grotesk font-bold text-main mt-1">{item.component}</p>
                  </div>

                  <div className="mt-5 border-t border-white/10 pt-4">
                    <div className="text-main-gray text-xs uppercase tracking-[1.5px]">
                      {t('whoWeHelp.labels.result')}
                    </div>
                    <p className="text-white text-sm mt-1">{item.result}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="mt-8 landingCardWide p-6">
            <div className="text-main-gray text-xs uppercase tracking-[1.5px]">
              {t('whoWeHelp.labels.packages')}
            </div>

            <div className="grid grid-cols-1 md2:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
              {segment.packages.map(findPackage).filter(Boolean).map((pkg) => (
                <div key={pkg.name}>
                  <div className="font-grotesk font-bold text-main">{pkg.name}</div>
                  <p className="text-main-gray text-sm mt-1">{pkg.deliverable}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-center mt-10">
        <a
          href={CALENDAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-transparent transition hover:bg-main border hover:text-black border-main text-main font-extrabold rounded-full px-6 py-2.5"
        >
          {t('whoWeHelp.cta')}
        </a>
      </div>
    </div>
  );
};

export default WhoWeHelp;
